export const SAMPLE_REPORT_URL = "https://reportgen.kittylaunch.com/sample-report";
export const ORDER_URL = "https://kittylaunch.com/website-report/checkout";
export const REPORT_PRICE = "$29";
export const SAMPLE_DOMAIN = "kittylaunch.example";

export type Impact = "high" | "medium" | "low";
export type RowStatus = "good" | "warning" | "critical";

export interface SamplePriority {
  impact: Impact;
  title: string;
  description: string;
  scope: string;
}

export const SAMPLE_REPORT_DATE = "September 24, 2026";
export const SAMPLE_SCORE = 84;
export const SAMPLE_AUDIT_AREAS = 24;

export const SAMPLE_PRIORITIES: SamplePriority[] = [
  { impact: "high", title: "Tighten the Content Security Policy", description: "Remove the legacy inline-style allowance, introduce a nonce-based policy and observe reports before enforcing the stricter rule.", scope: "1 protection" },
  { impact: "high", title: "Repair three unavailable internal URLs", description: "Restore useful destinations or update every internal reference so visitors and crawlers stop reaching 404 and 410 responses.", scope: "3 pages" },
  { impact: "high", title: "Fix four structured-data errors", description: "Correct the required product, article and breadcrumb fields, then validate representative pages from each affected template.", scope: "4 errors" },
  { impact: "medium", title: "Restore the missing submission-page canonical", description: "Add the preferred URL declaration and verify the submission workflow does not emit conflicting canonical targets.", scope: "1 page" },
  { impact: "medium", title: "Replace confirmed missing external resources", description: "Update or remove four unavailable scripts, images, fonts and stylesheets, then retest the pages that reference them.", scope: "4 resources" },
  { impact: "medium", title: "Correct five H1 hierarchy issues", description: "Add a clear primary heading to two pages and remove duplicate H1s from three shared templates.", scope: "5 pages" },
  { impact: "low", title: "Expand seven short meta descriptions", description: "Write page-specific summaries that explain the result and reason to visit without repeating the page title.", scope: "7 pages" },
  { impact: "low", title: "Review first-party images without alt text", description: "Start with diagrams and product screenshots, add concise alternatives for meaningful images and leave decorative assets empty.", scope: "18 images" },
];

export interface SpamRow {
  page: string;
  areas: string[];
  evidence: string;
  status: RowStatus;
  label: string;
}

export const SPAM_TOTALS = { flagged: 10, meta: 4, headings: 3, content: 6 };

export const SPAM_ROWS: SpamRow[] = [
  { page: "/categories/artificial-intelligence", areas: ["Meta tags", "Main content"], evidence: "‘AI tools’ appears 34 times; the title and description repeat the exact phrase.", status: "critical", label: "Multiple signals" },
  { page: "/pricing", areas: ["Meta tags", "Headings"], evidence: "‘KittyLaunch pricing’ is repeated in the title, description, H1 and two H2 headings.", status: "critical", label: "Multiple signals" },
  { page: "/blog/write-a-product-tagline", areas: ["Meta tags", "Main content"], evidence: "The exact phrase ‘product tagline’ appears in both metadata and 26 body locations.", status: "critical", label: "Multiple signals" },
  { page: "/products/pixel-proof", areas: ["Headings", "Main content"], evidence: "Four headings begin with ‘visual regression’, repeated 12 times overall.", status: "critical", label: "Multiple signals" },
  { page: "/blog/first-100-users", areas: ["Headings"], evidence: "Five consecutive H2 headings include the phrase ‘first 100 users’.", status: "warning", label: "Review" },
  { page: "/products/briefly-ai", areas: ["Main content"], evidence: "‘AI meeting brief’ appears 8 times in a very short page.", status: "warning", label: "Review" },
];

export const SPAM_SIGNALS = [
  { title: "Keyword stuffing", text: "The same phrase repeated again and again on one page." },
  { title: "Stuffed headings", text: "Headings packed with keywords instead of guiding readers." },
  { title: "Over-written titles and meta", text: "Titles and descriptions crammed with search terms." },
  { title: "Unnatural formatting", text: "Bold text and repeated elements used to push keywords." },
  { title: "Thin, mass-produced pages", text: "Many near-empty pages built from the same template." },
  { title: "Leftover placeholder text", text: "Lorem ipsum and dummy copy still live on your site." },
];

export interface MetaRow {
  page: string;
  description: string;
  status: RowStatus;
}

export const META_PAGES_CHECKED = 43;

export const META_ROWS: MetaRow[] = [
  { page: "/contact", description: "Questions, corrections or account support? Contact KittyLaunch.", status: "warning" },
  { page: "/newsletter", description: "A concise weekly briefing for product-minded teams.", status: "warning" },
  { page: "/categories/analytics", description: "Clear analytics products for better product decisions.", status: "warning" },
  { page: "/products/loopboard", description: "Structured async reviews for distributed creative teams.", status: "warning" },
  { page: "/products/signal-page", description: "A focused status page workflow for small product teams.", status: "warning" },
  { page: "/products/threadline", description: "Turn long discussions into traceable team decisions.", status: "warning" },
  { page: "/account/sign-in", description: "Sign in to your KittyLaunch account.", status: "warning" },
  { page: "/", description: "Discover useful new products, practical launch stories and independent reviews curated for founders, makers and early adopters.", status: "good" },
  { page: "/discover", description: "Browse recently launched software, compare clear product summaries and save promising tools for your next project.", status: "good" },
  { page: "/launches/today", description: "See the products launching today, follow maker updates and explore the ideas attracting early community attention.", status: "good" },
  { page: "/categories/developer-tools", description: "Compare APIs, deployment platforms, testing tools and observability products built for reliable software delivery.", status: "good" },
  { page: "/categories/productivity", description: "Find planning, writing, automation and collaboration products designed to reduce busywork and protect focused time.", status: "good" },
];

export const META_TOTALS: Record<RowStatus, { label: string; count: number }> = {
  critical: { label: "Missing or broken", count: 0 },
  warning: { label: "Too short", count: 7 },
  good: { label: "Looks good", count: 36 },
};

export const PAGE_SPEED = {
  totals: { critical: 2, warning: 7, good: 21 },
  rows: [
    { page: "/categories/productivity", score: 68, status: "critical" as const },
    { page: "/categories/design-tools", score: 76, status: "warning" as const },
    { page: "/categories/artificial-intelligence", score: 79, status: "warning" as const },
    { page: "/", score: 96, status: "good" as const },
  ],
};

export const COMPETITORS = [
  { domain: "launchscope.example", keywords: 186 },
  { domain: "productbrief.example", keywords: 154 },
  { domain: "makerlist.example", keywords: 132 },
  { domain: "newstack.example", keywords: 107 },
  { domain: "toolradar.example", keywords: 91 },
  { domain: SAMPLE_DOMAIN, keywords: 78, you: true },
];

export type CheckViewId = "seo" | "technical" | "security";

export const CHECK_VIEWS: Array<{ id: CheckViewId; label: string; summary: string; checks: Array<{ name: string; text: string }> }> = [
  {
    id: "seo",
    label: "SEO Audit",
    summary: "How search engines and people see your pages.",
    checks: [
      { name: "Page titles", text: "Missing, duplicate, too long or too short." },
      { name: "Meta descriptions", text: "Blank or weak summaries in search results." },
      { name: "Headings", text: "Missing or multiple H1s and a broken hierarchy." },
      { name: "Image alt text", text: "Meaningful images with no description." },
      { name: "Social sharing", text: "Open Graph tags that control link previews." },
      { name: "Thin content", text: "Pages with too little useful content." },
      { name: "Spam risk", text: "Keyword stuffing and over-optimization." },
      { name: "Placeholder text", text: "Lorem ipsum left on live pages." },
    ],
  },
  {
    id: "technical",
    label: "Technical SEO",
    summary: "Whether search engines can crawl, load and trust your site.",
    checks: [
      { name: "Indexing", text: "Pages blocked from Google by mistake." },
      { name: "XML sitemap", text: "Whether search engines can find every page." },
      { name: "Canonical URLs", text: "Duplicate versions of the same page." },
      { name: "Page speed", text: "The slowest pages and what slows them down." },
      { name: "Broken links", text: "Links that lead visitors to a dead end." },
      { name: "Redirects", text: "Internal links that take extra hops." },
      { name: "Error pages", text: "URLs that fail to load for visitors." },
      { name: "Structured data", text: "Schema errors that block rich results." },
      { name: "HTML validation", text: "Broken markup repeated across templates." },
      { name: "Missing files", text: "Images, scripts and styles that fail to load." },
    ],
  },
  {
    id: "security",
    label: "Security & Trust",
    summary: "Browser protection and whether your emails can be trusted.",
    checks: [
      { name: "HTTPS enforcement", text: "Whether browsers always use a secure connection." },
      { name: "Content Security Policy", text: "Limits which scripts can run on your pages." },
      { name: "Clickjacking protection", text: "Stops other sites from framing your pages." },
      { name: "Cookie security", text: "Secure, HttpOnly and SameSite settings." },
      { name: "Cross-origin access", text: "Which other sites can read your responses." },
      { name: "SPF, DKIM and DMARC", text: "Whether your emails land in inboxes, not spam." },
      { name: "DNS and certificates", text: "DNSSEC, CAA and mail routing records." },
    ],
  },
];

export const SECURITY_ROWS: Array<{ name: string; value: string; status: RowStatus | "neutral"; label: string }> = [
  { name: "Content-Security-Policy", value: "Allows inline styles for a legacy widget, no reporting endpoint", status: "warning", label: "Review" },
  { name: "Permissions-Policy", value: "Geolocation and payment are not explicitly restricted", status: "warning", label: "Review" },
  { name: "Strict-Transport-Security", value: "One-year max-age with includeSubDomains", status: "good", label: "Passed" },
  { name: "Session cookies", value: "Secure, HttpOnly and SameSite=Lax", status: "good", label: "Passed" },
  { name: "DMARC", value: "p=quarantine with aggregate reporting", status: "good", label: "Passed" },
  { name: "DNSSEC", value: "Not enabled, optional for this domain", status: "neutral", label: "Info" },
];

export const SAMPLE_PROMPT = `You are a senior application-security and platform engineer. Work in the website's actual repository and infrastructure configuration, not from assumptions alone.

## Objective
Assess the reported browser-protection and response-header findings, and prepare the smallest production-safe remediation.

## Audit context
- Site: ${SAMPLE_DOMAIN}
- Audit section: Security headers & browser protection
- This is a saved audit snapshot. Reproduce findings before changing production behavior.

## Safety and decision rules
- Treat scanner findings as evidence to validate, not proof of a vulnerability.
- Do not invent a Content-Security-Policy allowlist. Derive required origins from the application.
- Include rollback guidance for changes that can block scripts, frames or network requests.

## Audit evidence
<audit_evidence>
Finding group 1: Content-Security-Policy needs review
  What was found: The policy allows inline styles for a legacy widget and has no reporting endpoint.
  Recommended action: Replace the legacy inline style, add a nonce-based policy and monitor reports before enforcing it.
Finding group 2: Permissions-Policy needs review
  What was found: Camera and microphone are disabled, but geolocation and payment are not explicitly restricted.
  Recommended action: Add geolocation=() and payment=() unless a documented product flow requires them.
</audit_evidence>

## Required output
1. Assessment of each finding
2. Remediation plan, smallest safe change first
3. Code or configuration patches
4. Verification steps and rollback`;

export const FAQS = [
  {
    question: "What exactly do I get?",
    answer: "A private, interactive report of your website, plus PDF and Markdown downloads. It covers SEO, spam risk, technical health, security and your top search competitors, with a ranked action plan at the top.",
  },
  {
    question: "Is the report made by a person or a tool?",
    answer: "By a person, with help from our own system. The system collects data from every page. A KittyLaunch expert then works through your site by hand, confirms each issue, removes false alarms, finds the root causes, ranks what to fix first and writes the next steps.",
  },
  {
    question: "How long does it take?",
    answer: "Your report is delivered within 3 to 5 business days. That time goes into the hands-on work our expert does on your site. We email you as soon as it is ready.",
  },
  {
    question: "Do you need access to my website or code?",
    answer: "No. We only check what is publicly visible, the same way search engines and visitors see your site. No login, plugin or code access is needed.",
  },
  {
    question: "Can a report really tell me if my pages look like spam?",
    answer: "It shows the patterns search engines associate with spam, such as keyword stuffing, stuffed headings and thin template pages. Every flag comes with the exact page and what we found, so you can see the evidence and decide what to change.",
  },
  {
    question: "Will you fix the issues for me?",
    answer: "The report tells you exactly what to fix and in what order. Each section also includes a ready-made prompt you can paste into an AI coding tool to make the changes. We do not edit your site.",
  },
  {
    question: "Who can see my report?",
    answer: "Only you. Your report is locked to the email address you order with, and opening it requires a one-time code sent to that inbox. It is also hidden from search engines.",
  },
  {
    question: "Is this a monitoring subscription?",
    answer: "No. You pay once and get a detailed snapshot of your site on the day we check it. If you make big changes later, you can order a new report.",
  },
];
